import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
    addDays,
    eachDayOfInterval,
    eachMonthOfInterval,
    eachWeekOfInterval,
    endOfMonth,
    format,
    max as maxDate,
    parseISO,
    subDays,
} from "date-fns"
import { ChartData, ChartOptions } from "chart.js"
import { useNavigate } from "react-router-dom"
import {
    applyRestaurantMenuAnalyticsDrilldown,
    getRestaurantMenuAnalyticsErrorMessage,
    resetRestaurantMenuAnalyticsFilters,
    selectRestaurantMenuAnalyticsFilters,
    setRestaurantMenuAnalyticsMenuIds,
    setRestaurantMenuAnalyticsPeriod,
    setRestaurantMenuAnalyticsStatuses,
} from "@/features/restaurant-menu-analytics"
import {
    getOrdersSummary,
    getOrdersTimeSeries,
    IRestaurantMenuOrderSummaryResponse,
    IRestaurantMenuOrderTimeSeriesResponse,
} from "@/shared/api/restaurant-menu-orders"
import { IRestaurantMenu, listRestaurantMenus } from "@/shared/api/restaurant-menus"
import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import { ChartBlock } from "@/shared/ui/chart-block"
import { Loader } from "@/shared/ui/loader"
import { getPreviousPeriod, PeriodPicker, PeriodValue } from "@/shared/ui/period-picker"
import { Select } from "@/shared/ui/select"
import { PageHeader } from "@/widgets/page-header"

const ALL_OPTION_VALUE = "__all__"
// Canonical menu-order statuses (order.go domain @ N4): draft/processing/accepted/completed/cancelled/failed.
const COMMON_STATUSES = ["draft", "processing", "accepted", "completed", "cancelled", "failed"]
const FAILED_STATUS_PARTS = ["cancel", "fail", "error", "reject", "отмен", "ошиб"]

type LoadStatus = "idle" | "loading" | "loaded" | "error"

const formatMoney = (value: number): string => new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
}).format(value)

const formatNumber = (value: number | string): string => {
    const normalized = Number(value)
    return Number.isFinite(normalized) ? new Intl.NumberFormat("ru-RU").format(normalized) : String(value)
}

const isFailedStatus = (status?: string): boolean => {
    const normalized = status?.toLowerCase() || ""
    return FAILED_STATUS_PARTS.some((part) => normalized.includes(part))
}

const getDelta = (current: number, previous?: number): { label: string; className: string } => {
    if (typeof previous !== "number" || !Number.isFinite(previous) || previous === 0) {
        return { label: "н/д", className: "text-[#828282]" }
    }

    const value = ((current - previous) / Math.abs(previous)) * 100
    const rounded = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 1 }).format(Math.abs(value))

    if (value > 0) return { label: `↑ ${rounded} %`, className: "text-color-primary" }
    if (value < 0) return { label: `↓ ${rounded} %`, className: "text-color-danger" }
    return { label: "→ 0 %", className: "text-[#5F666C]" }
}

interface MetricCardProps {
    label: string
    value: string
    delta?: { label: string; className: string }
    onClick: () => void
}

const MetricCard = ({ label, value, delta, onClick }: MetricCardProps) => (
    <button
        type="button"
        onClick={onClick}
        className="box min-h-[150px] w-full text-left transition hover:-translate-y-0.5 hover:shadow-md"
    >
        <div className="text-base text-[#5F666C]">{label}</div>
        <div className="mt-3 text-2xl font-semibold screen:text-[32px]">{value}</div>
        {delta && (
            <div className={`mt-3 text-sm font-semibold ${delta.className}`}>
                {delta.label} к предыдущему периоду
            </div>
        )}
    </button>
)

const clampDate = (date: Date, minDate: Date, maxDate: Date): Date => {
    if (date.getTime() < minDate.getTime()) return minDate
    if (date.getTime() > maxDate.getTime()) return maxDate
    return date
}

const buildBucketPeriod = (
    bucketStart: string,
    sourcePeriod: PeriodValue,
    interval: "day" | "week" | "month"
): PeriodValue => {
    const periodFrom = parseISO(sourcePeriod.from)
    const periodTo = parseISO(sourcePeriod.to)
    // Compute the bucket end from the UNCLAMPED start (a week/month bucket may begin before the
    // period, e.g. a mid-week rolling preset), then clamp both bounds into the period.
    const rawBucketFrom = parseISO(bucketStart)
    const rawBucketTo = interval === "week"
        ? addDays(rawBucketFrom, 6)
        : interval === "month"
            ? endOfMonth(rawBucketFrom)
            : rawBucketFrom
    const bucketFrom = clampDate(rawBucketFrom, periodFrom, periodTo)
    const bucketTo = clampDate(rawBucketTo, periodFrom, periodTo)

    return {
        preset: "custom",
        from: format(bucketFrom, "yyyy-MM-dd"),
        to: format(bucketTo, "yyyy-MM-dd"),
        interval: sourcePeriod.interval,
    }
}

export const RestaurantMenuStatisticsPage = () => {
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const filters = useAppSelector(selectRestaurantMenuAnalyticsFilters)
    const summaryRequestId = useRef(0)
    const chartRequestId = useRef(0)
    const [summaryStatus, setSummaryStatus] = useState<LoadStatus>("idle")
    const [summary, setSummary] = useState<IRestaurantMenuOrderSummaryResponse | null>(null)
    const [summaryError, setSummaryError] = useState<string | null>(null)
    const [previousSummary, setPreviousSummary] = useState<IRestaurantMenuOrderSummaryResponse | null>(null)
    const [previousSummaryStatus, setPreviousSummaryStatus] = useState<LoadStatus>("idle")
    const [timeSeriesStatus, setTimeSeriesStatus] = useState<LoadStatus>("idle")
    const [timeSeries, setTimeSeries] = useState<IRestaurantMenuOrderTimeSeriesResponse | null>(null)
    const [timeSeriesError, setTimeSeriesError] = useState<string | null>(null)
    const [menus, setMenus] = useState<IRestaurantMenu[]>([])
    const [menuCount, setMenuCount] = useState<number | null>(null)
    const [menusStatus, setMenusStatus] = useState<LoadStatus>("idle")
    const [menusError, setMenusError] = useState<string | null>(null)

    const fetchMenus = useCallback(async () => {
        setMenusStatus("loading")
        setMenusError(null)

        try {
            const response = await listRestaurantMenus()
            setMenus(response.data.menus)
            setMenuCount(response.data.context.menu_count)
            setMenusStatus("loaded")
        } catch (error) {
            setMenus([])
            setMenuCount(null)
            setMenusStatus("error")
            setMenusError(getRestaurantMenuAnalyticsErrorMessage(error))
        }
    }, [])

    useEffect(() => {
        fetchMenus()
    }, [fetchMenus])

    const fetchSummaries = useCallback(() => {
        const requestId = ++summaryRequestId.current
        const previousPeriod = getPreviousPeriod(filters.period)
        const commonQuery = {
            menuIds: filters.menuIds,
            statuses: filters.statuses,
        }

        setSummaryStatus("loading")
        setSummary(null)
        setSummaryError(null)
        setPreviousSummaryStatus("loading")
        setPreviousSummary(null)

        getOrdersSummary({
            ...commonQuery,
            periodFrom: filters.period.from,
            periodTo: filters.period.to,
        }).then((response) => {
            if (summaryRequestId.current !== requestId) return
            setSummary(response.data)
            setSummaryStatus("loaded")
        }).catch((error) => {
            if (summaryRequestId.current !== requestId) return
            setSummary(null)
            setSummaryStatus("error")
            setSummaryError(getRestaurantMenuAnalyticsErrorMessage(error))
        })

        getOrdersSummary({
            ...commonQuery,
            periodFrom: previousPeriod.from,
            periodTo: previousPeriod.to,
        }).then((response) => {
            if (summaryRequestId.current !== requestId) return
            setPreviousSummary(response.data)
            setPreviousSummaryStatus("loaded")
        }).catch(() => {
            if (summaryRequestId.current !== requestId) return
            setPreviousSummary(null)
            setPreviousSummaryStatus("error")
        })
    }, [filters.menuIds, filters.period, filters.statuses])

    useEffect(() => {
        fetchSummaries()
    }, [fetchSummaries])

    const fetchTimeSeries = useCallback(() => {
        const requestId = ++chartRequestId.current
        setTimeSeriesStatus("loading")
        setTimeSeries(null)
        setTimeSeriesError(null)

        getOrdersTimeSeries({
            periodFrom: filters.period.from,
            periodTo: filters.period.to,
            menuIds: filters.menuIds,
            statuses: filters.statuses,
            interval: filters.period.interval,
        }).then((response) => {
            if (chartRequestId.current !== requestId) return
            setTimeSeries(response.data)
            setTimeSeriesStatus("loaded")
        }).catch((error) => {
            if (chartRequestId.current !== requestId) return
            setTimeSeries(null)
            setTimeSeriesStatus("error")
            setTimeSeriesError(getRestaurantMenuAnalyticsErrorMessage(error))
        })
    }, [filters.menuIds, filters.period.from, filters.period.interval, filters.period.to, filters.statuses])

    useEffect(() => {
        fetchTimeSeries()
    }, [fetchTimeSeries])

    const menuOptions = useMemo(() => {
        const loadedOptions = menus.filter((menu) => menu.id).map((menu) => ({
            value: menu.id as string,
            label: menu.name || menu.id as string,
        }))
        const loadedIds = new Set(loadedOptions.map((option) => option.value))
        const persistedOptions = filters.menuIds
            .filter((menuId) => !loadedIds.has(menuId))
            .map((menuId) => ({ value: menuId, label: menuId }))

        return [
            { value: ALL_OPTION_VALUE, label: "Все меню" },
            ...loadedOptions,
            ...persistedOptions,
        ]
    }, [filters.menuIds, menus])

    const selectedMenuOptions = useMemo(
        () => menuOptions.filter((option) => option.value !== ALL_OPTION_VALUE && filters.menuIds.includes(option.value)),
        [filters.menuIds, menuOptions]
    )

    const statusOptions = useMemo(() => {
        const values = new Set([
            ...COMMON_STATUSES,
            ...filters.statuses,
            ...(summary?.statusBreakdown.map((bucket) => bucket.status).filter(Boolean) as string[] || []),
        ])
        return [
            { value: ALL_OPTION_VALUE, label: "Все статусы" },
            ...Array.from(values).map((status) => ({ value: status, label: status })),
        ]
    }, [filters.statuses, summary])

    const selectedStatusOptions = useMemo(
        () => statusOptions.filter((option) => option.value !== ALL_OPTION_VALUE && filters.statuses.includes(option.value)),
        [filters.statuses, statusOptions]
    )

    const drillDown = useCallback((period: PeriodValue, statuses = filters.statuses) => {
        dispatch(applyRestaurantMenuAnalyticsDrilldown({
            period,
            menuIds: filters.menuIds,
            statuses,
            tableId: "",
        }))
        navigate("/restaurant-menus/orders")
    }, [dispatch, filters.menuIds, filters.statuses, navigate])

    const chartBuckets = useMemo(() => {
        if (!timeSeries) return []

        const periodFrom = parseISO(filters.period.from)
        const periodTo = parseISO(filters.period.to)
        const axisStart = timeSeries.periodTruncated
            ? maxDate([
                periodFrom,
                subDays(periodTo, timeSeries.limits.maxPeriodDays - 1),
            ])
            : periodFrom
        const interval = { start: axisStart, end: periodTo }
        const bucketStarts = filters.period.interval === "week"
            ? eachWeekOfInterval(interval, { weekStartsOn: 1 })
            : filters.period.interval === "month"
                ? eachMonthOfInterval(interval)
                : eachDayOfInterval(interval)
        const pointByDate = new Map(
            timeSeries.points
                .filter((point) => point.bucketStart !== null)
                .map((point) => [
                    format(new Date(point.bucketStart as string), "yyyy-MM-dd"),
                    point,
                ])
        )

        return bucketStarts.map((bucketStart) => {
            const point = pointByDate.get(format(bucketStart, "yyyy-MM-dd"))

            return {
                bucketStart,
                ordersCount: Number(point?.ordersCount ?? 0),
                ordersTotal: point?.ordersTotal ?? 0,
            }
        })
    }, [filters.period.from, filters.period.interval, filters.period.to, timeSeries])

    const chartData: ChartData<"bar"> = useMemo(() => ({
        labels: chartBuckets.map((bucket) => (
            new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "short", year: "numeric" }).format(bucket.bucketStart)
        )),
        datasets: [
            {
                label: "Число заказов",
                data: chartBuckets.map((bucket) => bucket.ordersCount),
                backgroundColor: "rgba(5, 205, 153, 0.75)",
                borderColor: "#05CD99",
                borderWidth: 1,
                borderRadius: 6,
                yAxisID: "orders",
            },
            {
                label: "Сумма заказов",
                data: chartBuckets.map((bucket) => bucket.ordersTotal),
                backgroundColor: "rgba(22, 47, 43, 0.72)",
                borderColor: "#162F2B",
                borderWidth: 1,
                borderRadius: 6,
                yAxisID: "money",
            },
        ],
    }), [chartBuckets])

    const chartOptions: ChartOptions<"bar"> = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            intersect: false,
            mode: "index",
        },
        plugins: {
            legend: {
                display: true,
                position: "bottom",
                labels: {
                    usePointStyle: true,
                    pointStyle: "circle",
                },
            },
        },
        scales: {
            x: {
                grid: { drawOnChartArea: false },
                border: { color: "rgba(0, 0, 0, 0)" },
            },
            orders: {
                beginAtZero: true,
                position: "left",
                ticks: { precision: 0 },
                border: { dash: [8, 4], color: "rgba(0, 0, 0, 0)" },
            },
            money: {
                beginAtZero: true,
                position: "right",
                grid: { drawOnChartArea: false },
                ticks: {
                    callback: (value) => `${new Intl.NumberFormat("ru-RU", { notation: "compact" }).format(Number(value))} ₽`,
                },
                border: { color: "rgba(0, 0, 0, 0)" },
            },
        },
        onClick: (_event, elements) => {
            const bucketIndex = elements[0]?.index
            const bucket = typeof bucketIndex === "number" ? chartBuckets[bucketIndex] : undefined
            if (!bucket) return
            drillDown(buildBucketPeriod(
                format(bucket.bucketStart, "yyyy-MM-dd"),
                filters.period,
                filters.period.interval
            ))
        },
    }), [chartBuckets, drillDown, filters.period])

    const ordersCount = Number(summary?.ordersCount ?? 0)
    const deltaComparable = previousSummaryStatus === "loaded"
        && !summary?.periodTruncated
        && !previousSummary?.periodTruncated
    const previousOrdersCount = deltaComparable
        ? Number(previousSummary?.ordersCount)
        : undefined
    const previousOrdersTotal = deltaComparable
        ? previousSummary?.ordersTotal
        : undefined
    const previousAverageCheck = deltaComparable
        ? previousSummary?.averageCheck
        : undefined
    const hasSummaryData = summaryStatus === "loaded" && ordersCount > 0
    const hasChartData = chartBuckets.some(
        (bucket) => bucket.ordersCount !== 0 || bucket.ordersTotal !== 0
    )
    const failedOrdersCount = summary?.statusBreakdown.reduce(
        (total, bucket) => total + (isFailedStatus(bucket.status) ? Number(bucket.ordersCount ?? 0) : 0),
        0
    ) ?? 0
    const failedShare = ordersCount > 0 ? (failedOrdersCount / ordersCount) * 100 : 0
    const appliedFiltersCount = 1 + Number(filters.menuIds.length > 0) + Number(filters.statuses.length > 0)

    return (
        <div className="_page_container">
            <PageHeader title="Статистика меню ресторанов" />
            <div className="flexbox">
                <div className="box">
                    <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                        <h2 className="text-lg font-bold screen:text-2xl">Период и фильтры</h2>
                        <div className="flex flex-wrap items-center gap-3">
                            <span className="text-sm text-[#5F666C]">Применено фильтров: {appliedFiltersCount}</span>
                            <button
                                type="button"
                                onClick={() => dispatch(resetRestaurantMenuAnalyticsFilters())}
                                className="form_cancel_button !h-10 !w-auto px-5"
                            >
                                Сбросить
                            </button>
                        </div>
                    </div>
                    <PeriodPicker
                        value={filters.period}
                        onChange={(period) => dispatch(setRestaurantMenuAnalyticsPeriod(period))}
                        withInterval
                    />
                    <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                        <Select
                            isMulti
                            isLoading={menusStatus === "loading"}
                            options={menuOptions}
                            value={selectedMenuOptions}
                            placeholder="Все меню"
                            onChange={(options) => {
                                const values = (options || []).map((option: { value: string }) => option.value)
                                dispatch(setRestaurantMenuAnalyticsMenuIds(
                                    values.includes(ALL_OPTION_VALUE) ? [] : values
                                ))
                            }}
                        />
                        <Select
                            isMulti
                            options={statusOptions}
                            value={selectedStatusOptions}
                            placeholder="Все статусы"
                            onChange={(options) => {
                                const values = (options || []).map((option: { value: string }) => option.value)
                                dispatch(setRestaurantMenuAnalyticsStatuses(
                                    values.includes(ALL_OPTION_VALUE) ? [] : values
                                ))
                            }}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                    {summaryStatus === "loading" && (
                        <div className="info_panel flex min-h-[150px] items-center justify-center md:col-span-2 xl:col-span-3">
                            <Loader />
                        </div>
                    )}
                    {summaryStatus === "error" && (
                        <div className="info_panel flex min-h-[150px] flex-wrap items-center justify-between gap-4 md:col-span-2 xl:col-span-3">
                            <span className="text-color-danger">{summaryError}</span>
                            <button type="button" onClick={fetchSummaries} className="form_handle_button !w-auto px-6">
                                Повторить
                            </button>
                        </div>
                    )}
                    {summaryStatus === "loaded" && !hasSummaryData && (
                        <div className="info_panel min-h-[150px] text-lg md:col-span-2 xl:col-span-3">
                            Нет данных за период
                        </div>
                    )}
                    {hasSummaryData && summary && (
                        <>
                            <MetricCard
                                label="Число заказов"
                                value={formatNumber(summary.ordersCount)}
                                delta={getDelta(ordersCount, previousOrdersCount)}
                                onClick={() => drillDown(filters.period)}
                            />
                            <MetricCard
                                label="Сумма заказов"
                                value={formatMoney(summary.ordersTotal)}
                                delta={getDelta(summary.ordersTotal, previousOrdersTotal)}
                                onClick={() => drillDown(filters.period)}
                            />
                            <MetricCard
                                label="Средний чек"
                                value={formatMoney(summary.averageCheck)}
                                delta={getDelta(summary.averageCheck, previousAverageCheck)}
                                onClick={() => drillDown(filters.period)}
                            />
                        </>
                    )}

                    {menusStatus === "loading" && (
                        <div className="info_panel flex min-h-[150px] items-center justify-center">
                            <Loader />
                        </div>
                    )}
                    {menusStatus === "error" && (
                        <div className="info_panel flex min-h-[150px] flex-col justify-between gap-3">
                            <span className="text-color-danger">{menusError}</span>
                            <button type="button" onClick={fetchMenus} className="form_handle_button !h-10 px-5">
                                Повторить
                            </button>
                        </div>
                    )}
                    {menusStatus === "loaded" && menuCount !== null && (
                        <MetricCard
                            label="Число меню"
                            value={formatNumber(menuCount)}
                            onClick={() => drillDown(filters.period)}
                        />
                    )}
                </div>

                {summary?.periodTruncated && (
                    <div className="info_panel !bg-[#FFF8E7]">
                        Показаны данные максимум за {summary.limits.maxPeriodDays} дней, период был ограничен сервером.
                    </div>
                )}
                {previousSummary?.periodTruncated && (
                    <div className="info_panel !bg-[#FFF8E7]">
                        Предыдущий период для сравнения был ограничен сервером до {previousSummary.limits.maxPeriodDays} дней.
                    </div>
                )}

                {hasSummaryData && summary && (
                    <div className="box">
                        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                            <h2 className="text-lg font-bold screen:text-2xl">Разбивка по статусам</h2>
                            <div className="rounded-full bg-[#FFF4F4] px-4 py-2 font-semibold text-color-danger">
                                Отменено / ошибки: {formatNumber(failedOrdersCount)} ({new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 1 }).format(failedShare)} %)
                            </div>
                        </div>
                        {summary.statusBreakdown.length === 0 ? (
                            <div className="text-[#828282]">Нет данных по статусам</div>
                        ) : (
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                                {summary.statusBreakdown.map((bucket, index) => (
                                    <button
                                        type="button"
                                        key={`${bucket.status || "status"}-${index}`}
                                        disabled={!bucket.status}
                                        onClick={() => bucket.status && drillDown(filters.period, [bucket.status])}
                                        className={`rounded-xl border p-4 text-left transition hover:bg-[#F7F8FA] disabled:cursor-default ${
                                            isFailedStatus(bucket.status) ? "border-[#F1B6B7] bg-[#FFF8F8]" : "border-[#E7EAEE]"
                                        }`}
                                    >
                                        <div className="break-all font-semibold">{bucket.status || "Без статуса"}</div>
                                        <div className="mt-2 text-xl">{formatNumber(bucket.ordersCount ?? 0)}</div>
                                        <div className="mt-1 text-sm text-[#5F666C]">{formatMoney(bucket.ordersTotal ?? 0)}</div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {timeSeries?.periodTruncated && (
                    <div className="info_panel !bg-[#FFF8E7]">
                        Для графика показаны данные максимум за {timeSeries.limits.maxPeriodDays} дней, период был ограничен сервером.
                    </div>
                )}

                {timeSeriesStatus === "loading" && (
                    <div className="box flex min-h-[400px] items-center justify-center">
                        <Loader />
                    </div>
                )}
                {timeSeriesStatus === "error" && (
                    <div className="box flex min-h-[220px] flex-wrap items-center justify-between gap-4">
                        <span className="text-color-danger">{timeSeriesError}</span>
                        <button type="button" onClick={fetchTimeSeries} className="form_handle_button !w-auto px-6">
                            Повторить
                        </button>
                    </div>
                )}
                {timeSeriesStatus === "loaded" && (
                    <ChartBlock
                        type="bar"
                        title="Заказы по времени"
                        data={chartData}
                        options={chartOptions}
                        chartClass="h-[420px]"
                        noData={!hasChartData}
                        noDataComponent={<div className="info_panel text-lg">Нет данных за период</div>}
                    />
                )}
            </div>
        </div>
    )
}
